import React, { useState, useEffect, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router';
import { Paper, Typography } from '@mui/material';
import { pink, cyan } from '@mui/material/colors';
import NewDashboard from './NewDashboard';
import Emails from './Emails';
import NewEmail from './NewEmail';
import Pages from './Pages';
import Surveys from './Surveys';
import Survey from './Survey';
import NewPage from './NewPage';
import TitleBar from './TitleBar';
import WebFont from 'webfontloader';
import LoadingOverlay from '../loadingOverlay/LoadingOverlay';
import useApi from '../hooks/useApi';
import usePrevious from '../hooks/usePrevious';
import { startOfDay } from 'date-fns';
import useSuspenseFetch from '../hooks/useSuspenseFetch';
import { getReportingPeriod, generatePeriods } from '../account/reportingPeriod/ReportingPeriods';
import SplashScreen from '../splashScreen/SplashScreen';

const NewReportsInner = () => {
    const location = useLocation();
    const { handleGet } = useApi();
    const [fonts, setFonts] = useState(null);
    const { data: user } = useSuspenseFetch('user');
    const { data: account } = useSuspenseFetch('account');
    const { data: topics } = useSuspenseFetch('topics');
    const [reportingPeriods, setReportingPeriods] = useState([]);
    const [selectedPeriods, setSelectedPeriods] = useState([]);
    const prevSelectedPeriods = usePrevious(selectedPeriods);
    const [compare, setCompare] = useState(false);
    const [tab, setTab] = useState('');
    const periodColours = [pink[600], cyan[600]];
    const [filterTopics, setFilterTopics] = useState([]);
    const prevFilterTopics = usePrevious(filterTopics);

    const titleBarHeight = 56;
    const topOffset = titleBarHeight + 64; // todo, pass the 64 from header?

    const hasManageReportsPermission = user.permissions.includes('Manage Reports');

    const handleFetchCustomFonts = async () => {
        const response = await handleGet('fonts');

        if (!response.ok) {
            return;
        }

        const data = await response.json();
        setFonts(data);
    };

    const handleLoadFonts = font => {
        if (font.type === 'google') {
            WebFont.load({
                google: {
                    families: [font.name]
                }
            });
        }
    };

    const handleChangeTopicsFilter = topic => {
        if (!topic) {
            setFilterTopics([]);
            return;
        }

        if (Boolean(filterTopics.find(r => r.id === topic.id))) {
            setFilterTopics(filterTopics.filter(r => r.id !== topic.id));
        }
        else {
            setFilterTopics([...filterTopics, topic]);
        }
    };

    useEffect(() => {
        (fonts && fonts.length > 0) && fonts.forEach((font) => handleLoadFonts(font));
    }, [fonts]);

    useEffect(() => {
        if (!location) {
            return;
        }

        if (location.pathname === '/newreports') {
            setTab('');
        }
        else {
            const tab = location.pathname.split('/')[2];
            tab && setTab(tab);
        }
    }, [location]);

    useEffect(() => {
        const reportingPeriod = getReportingPeriod(account);
        const reportingPeriods = generatePeriods(reportingPeriod, account.startDate);
        setReportingPeriods(reportingPeriods);
    }, [account]);

    useEffect(() => {
        const today = startOfDay(new Date());
        const currentPeriod = reportingPeriods.find(e => e[0] <= today && e[1] >= today);
        setSelectedPeriods([currentPeriod, currentPeriod]);
        setCompare(false);
    }, [reportingPeriods]);

    useEffect(() => {
        if (hasManageReportsPermission) {
            handleFetchCustomFonts();
        }
    }, []);

    if (!hasManageReportsPermission) {
        return (
            <Paper sx={{ padding: 6, textAlign: 'center' }}>
                <Typography>You don't have permission to manage Reports.</Typography>
                <Typography>Please contact your account administrator to have this enabled.</Typography>
            </Paper>
        );
    }

    if (!fonts) {
        return <LoadingOverlay />;
    }

    return (
        <>
            <TitleBar
                reportingPeriods={reportingPeriods}
                selectedPeriods={selectedPeriods}
                onSetSelectedPeriods={setSelectedPeriods}
                compare={compare}
                onSetCompare={setCompare}
                periodColours={periodColours}
                tab={tab}
                location={location}
                titleBarHeight={titleBarHeight}
                topics={topics}
                filterTopics={filterTopics}
                onChangeTopicsFilter={handleChangeTopicsFilter}
            />
            <Suspense fallback={<SplashScreen />}>
                <Routes>
                    <Route path="/" element={(
                        <NewDashboard
                            selectedPeriods={selectedPeriods}
                            prevSelectedPeriods={prevSelectedPeriods}
                            compare={compare}
                            periodColours={periodColours}
                            filterTopics={filterTopics}
                            prevFilterTopics={prevFilterTopics}
                        />
                    )} />
                    <Route path="emails" element={(
                        <Emails
                            reportingPeriods={reportingPeriods}
                            selectedPeriods={selectedPeriods}
                            prevSelectedPeriods={prevSelectedPeriods}
                            compare={compare}
                            periodColours={periodColours}
                            topOffset={topOffset}
                            filterTopics={filterTopics}
                            onChangeTopicsFilter={handleChangeTopicsFilter}
                            prevFilterTopics={prevFilterTopics}
                        />
                    )} />
                    <Route path="emails/insights" element={(
                        <Emails
                            reportingPeriods={reportingPeriods}
                            selectedPeriods={selectedPeriods}
                            prevSelectedPeriods={prevSelectedPeriods}
                            compare={compare}
                            periodColours={periodColours}
                            topOffset={topOffset}
                            filterTopics={filterTopics}
                            onChangeTopicsFilter={handleChangeTopicsFilter}
                            prevFilterTopics={prevFilterTopics}
                        />
                    )} />
                    <Route path="emails/:id" element={<NewEmail />} />
                    <Route path="pages" element={(
                        <Pages
                            reportingPeriods={reportingPeriods}
                            selectedPeriods={selectedPeriods}
                            prevSelectedPeriods={prevSelectedPeriods}
                            compare={compare}
                            topOffset={topOffset}
                            filterTopics={filterTopics}
                            onChangeTopicsFilter={handleChangeTopicsFilter}
                            prevFilterTopics={prevFilterTopics}
                        />
                    )} />
                    <Route path="pages/:id" element={<NewPage />} />
                    <Route path="surveys" element={(
                        <Surveys
                            reportingPeriods={reportingPeriods}
                            selectedPeriods={selectedPeriods}
                            prevSelectedPeriods={prevSelectedPeriods}
                            compare={compare}
                            topOffset={topOffset}
                            filterTopics={filterTopics}
                            onChangeTopicsFilter={handleChangeTopicsFilter}
                            prevFilterTopics={prevFilterTopics}
                        />
                    )} />
                    <Route path="surveys/:id" element={<Survey />} />
                </Routes>
            </Suspense>
        </>
    );
};

// todo the following is needed because React Router 7 requires each route to have its own Suspense boundary
// see https://github.com/remix-run/react-router/issues/10568
// inner component can be avoided by only loading resources where they are needed instead of drilling them like this

const NewReports = () => {
    return (
        <Suspense fallback={<SplashScreen />}>
            <NewReportsInner />
        </Suspense>
    );
};

export default NewReports;